import React, {useMemo, useRef} from 'react'

import {
  Wrapper,
  LabelRow,
  LabelIcon,
  LabelEl,
  InputWrapper,
  StyledInput,
  IconSlot,
  HelperText,
  InfoRow,
  InfoText,
  InfoLink,
  MetaRow,
  LabelReq,
  StyledLinkIcon,
  ActionSlot,
} from './Input.styles'

// export function InfoIcon() {
//   return <span aria-hidden="true">ⓘ</span>
// }

export function LinkIcon() {
  return <span aria-hidden="true">↗</span>
}

// ─────────────────────────────────────────────
// React 17-compatible unique id
// ─────────────────────────────────────────────

let idCounter = 0

function useUniqueId(prefix = 'input') {
  const idRef = useRef(null)

  if (idRef.current === null) {
    idRef.current = `${prefix}-${++idCounter}`
  }

  return idRef.current
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export function Input({
  label,

  labelLeftIcon,

  helperText,

  infoText,

  infoLink,

  infoLinkLabel,

  infoIcon,

  linkIcon,

  errorText,

  leftIcon,

  rightIcon,

  onLeftIconClick,

  onRightIconClick,

  rightAction,

  size = 'sm',

  variant = 'default',

  disabled = false,

  required = false,

  id: externalId,

  className,

  style,

  ...nativeProps
}) {
  const autoId = useUniqueId('input')

  const inputId = externalId ?? autoId

  // Unique ids
  const helperId = `${inputId}-helper`
  const errorId = `${inputId}-error`
  const infoTextId = `${inputId}-info-text`
  const infoLinkId = `${inputId}-info-link`

  const hasError = Boolean(errorText)

  // Build aria-describedby safely
  const describedBy = useMemo(() => {
    return [
      helperText ? helperId : null,
      errorText ? errorId : null,
      infoText ? infoTextId : null,
      infoLink ? infoLinkId : null,
    ]
      .filter(Boolean)
      .join(' ')
  }, [
    helperText,
    errorText,
    infoText,
    infoLink,
    helperId,
    errorId,
    infoTextId,
    infoLinkId,
  ])

  const clickableLeftIcon = Boolean(leftIcon && onLeftIconClick)
  const clickableRightIcon = Boolean(rightIcon && onRightIconClick)
  const clickableRightAction = Boolean(rightAction)

  return (
    <Wrapper className={className} style={style}>
      {label && (
        <LabelRow>
          {labelLeftIcon && <LabelIcon>{labelLeftIcon}</LabelIcon>}

          <LabelEl htmlFor={inputId} variant={variant}>
            {label}
          </LabelEl>

          {required && (
            <LabelReq htmlFor={inputId} variant={variant}>
              *
            </LabelReq>
          )}
        </LabelRow>
      )}

      <InputWrapper hasError={hasError} variant={variant}>
        {leftIcon && (
          <IconSlot
            side="left"
            clickable={clickableLeftIcon}
            onClick={onLeftIconClick}
            role={clickableLeftIcon ? 'button' : undefined}
            tabIndex={clickableLeftIcon ? 0 : undefined}
            aria-label={clickableLeftIcon ? 'Input action button' : undefined}
          >
            {leftIcon}
          </IconSlot>
        )}

        <StyledInput
          id={inputId}
          size={size}
          variant={variant}
          disabled={disabled}
          hasError={hasError}
          hasLeftIcon={Boolean(leftIcon)}
          hasRightIcon={Boolean(rightIcon)}
          aria-invalid={hasError}
          aria-describedby={describedBy || undefined}
          {...nativeProps}
        />

        {rightIcon && (
          <IconSlot
            side="right"
            clickable={clickableRightIcon}
            onClick={onRightIconClick}
            role={clickableRightIcon ? 'button' : undefined}
            tabIndex={clickableRightIcon ? 0 : undefined}
            aria-label={clickableRightIcon ? 'Input action button' : undefined}
          >
            {rightIcon}
          </IconSlot>
        )}

        {rightAction && (
          <ActionSlot
            side="right"
            clickable={clickableRightAction}
            tabIndex={clickableRightAction ? 0 : undefined}
            aria-label={
              clickableRightAction ? 'Input action button' : undefined
            }
          >
            {rightAction}
          </ActionSlot>
        )}
      </InputWrapper>

      {/* Info Section */}
      {(infoText || infoLink) && (
        <MetaRow>
          {infoText && (
            <InfoRow id={infoTextId}>
              {/* {infoIcon ?? <StyledInfoIcon />} */}

              <InfoText variant={variant}>{infoText}</InfoText>
            </InfoRow>
          )}

          {infoLink && (
            <InfoRow id={infoLinkId}>
              {linkIcon ?? (
                <StyledLinkIcon>
                  <LinkIcon />
                </StyledLinkIcon>
              )}

              <InfoLink
                href={infoLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {infoLinkLabel}
              </InfoLink>
            </InfoRow>
          )}
        </MetaRow>
      )}

      {hasError ? (
        <HelperText id={errorId} isError role="alert">
          {errorText}
        </HelperText>
      ) : (
        helperText && <HelperText id={helperId}>{helperText}</HelperText>
      )}
    </Wrapper>
  )
}

// import React, {useRef} from 'react'

// import {
//   Wrapper,
//   LabelRow,
//   LabelIcon,
//   LabelEl,
//   InputWrapper,
//   StyledInput,
//   IconSlot,
//   HelperText,
// } from './Input.styles'

// // ─── React 17-compatible unique id ───────────────────────────────────────────
// // useId() is React 18+ only. This ref-based counter is stable across renders
// // and safe for SSR since it never produces conflicting ids.

// let idCounter = 0

// function useUniqueId(prefix = 'input') {
//   const idRef = useRef(null)

//   if (idRef.current === null) {
//     idRef.current = `${prefix}-${++idCounter}`
//   }

//   return idRef.current
// }

// // ─────────────────────────────────────────────
// // Component
// // ─────────────────────────────────────────────

// /**
//  * Input
//  *
//  * @param {string}    label            - visible label text
//  * @param {string}    helperText       - hint shown below the input
//  * @param {string}    errorText        - error message; also triggers red border
//  * @param {ReactNode} leftIcon         - icon on the left inside the input
//  * @param {ReactNode} rightIcon        - icon on the right inside the input
//  * @param {Function}  onRightIconClick - makes right icon a clickable button
//  * @param {'sm'|'md'|'lg'} size
//  * @param {boolean}   disabled
//  *
//  * All native <input> props pass through (type, value, onChange, placeholder…)
//  */
// export function Input({
//   label,

//   labelLeftIcon,

//   helperText,

//   infoText,

//   infoLink,

//   errorText,

//   leftIcon,

//   rightIcon,

//   onRightIconClick,

//   size = 'sm',

//   variant = 'default',

//   disabled = false,

//   id: externalId,

//   ...nativeProps
// }) {
//   const autoId = useUniqueId('input')

//   const inputId = externalId ?? autoId

//   const helperId = `${inputId}-helper`

//   const errorId = `${inputId}-error`

//   const hasError = Boolean(errorText)

//   const hasInfoText = Boolean(infoText)

//   const hasInfoLink = Boolean(infoLink)

//   const hasLeftIcon = Boolean(leftIcon)

//   const hasRightIcon = Boolean(rightIcon)

//   const hasLabelLeftIcon = Boolean(labelLeftIcon)

//   return (
//     <Wrapper>
//       {label && (
//         <LabelRow>
//           {hasLabelLeftIcon && <LabelIcon>{labelLeftIcon}</LabelIcon>}

//           <LabelEl htmlFor={inputId} variant={variant}>
//             {label}
//           </LabelEl>
//         </LabelRow>
//       )}

//       <InputWrapper>
//         {hasLeftIcon && (
//           <IconSlot side="left" aria-hidden="true">
//             {leftIcon}
//           </IconSlot>
//         )}

//         <StyledInput
//           id={inputId}
//           size={size}
//           variant={variant}
//           disabled={disabled}
//           hasError={hasError}
//           hasLeftIcon={hasLeftIcon}
//           hasRightIcon={hasRightIcon}
//           aria-invalid={hasError}
//           aria-describedby={
//             [errorText && errorId, helperText && helperId]
//               .filter(Boolean)
//               .join(' ') || undefined
//           }
//           {...nativeProps}
//         />

//         {hasRightIcon && (
//           <IconSlot
//             side="right"
//             clickable={Boolean(onRightIconClick)}
//             onClick={onRightIconClick}
//             aria-label={onRightIconClick ? 'Toggle input action' : undefined}
//             role={onRightIconClick ? 'button' : undefined}
//             tabIndex={onRightIconClick ? 0 : undefined}
//           >
//             {rightIcon}
//           </IconSlot>
//         )}
//       </InputWrapper>

//       {hasInfoText && (
//         <InfoText id={helperId} role="info">
//           {infoText}
//         </InfoText>
//       )}
//       {hasInfoLink && (
//         <InfoLink id={helperId} role="link">
//           {infoLink}
//         </InfoLink>
//       )}
//       {hasError && (
//         <HelperText id={errorId} isError role="alert">
//           {errorText}
//         </HelperText>
//       )}

//       {!hasError && helperText && (
//         <HelperText id={helperId}>{helperText}</HelperText>
//       )}
//     </Wrapper>
//   )
// }
